/**
 * Generated by orval
 * Do not edit manually.
 * IP Fabric API
 * IP Fabric API documentation. For details see [IP Fabric API documentation](https://docs.ipfabric.io/main/IP_Fabric_API/)
 */
import { useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  PostTablesInventoryDevices200,
  PostTablesInventoryDevicesBody,
  PostTablesInventorySummaryFamilies200,
  PostTablesInventorySummaryFamiliesBody,
  PostTablesInventorySummaryModels200,
  PostTablesInventorySummaryModelsBody,
  PostTablesInventorySummaryPlatforms200,
  PostTablesInventorySummaryPlatformsBody,
  PostTablesInventorySummaryVendors200,
  PostTablesInventorySummaryVendorsBody,
} from '../../schemas';

import { apiClient } from '../../../apiClient';

/**
 * Inventory of all logical managed network infrastructure devices that have been discovered and analyzed.

This table can be used for (but it is not limited to):

* Verifying of the vendors and platforms of the network devices
* Verifying of the operating system versions and images
* Verifying if the boot settings (config register) are applied correctly
* Verifying if the devices are stable and being rebooted often
* Serial number and management IP address overview
* Adherence to naming conventions (for device hostnames)
 * @summary Device Inventory
 */
export const postTablesInventoryDevices = (
  postTablesInventoryDevicesBody: PostTablesInventoryDevicesBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesInventoryDevices200>({
    url: `/tables/inventory/devices`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesInventoryDevicesBody,
    signal,
  });
};

export const getPostTablesInventoryDevicesQueryKey = (
  postTablesInventoryDevicesBody: PostTablesInventoryDevicesBody,
) => {
  return [`/tables/inventory/devices`, postTablesInventoryDevicesBody] as const;
};

export const getPostTablesInventoryDevicesQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesInventoryDevices>>,
  TError = unknown,
>(
  postTablesInventoryDevicesBody: PostTablesInventoryDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryDevices>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesInventoryDevicesQueryKey(postTablesInventoryDevicesBody);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesInventoryDevices>>
  > = ({ signal }) =>
    postTablesInventoryDevices(postTablesInventoryDevicesBody, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesInventoryDevices>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesInventoryDevicesQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesInventoryDevices>>
>;
export type PostTablesInventoryDevicesQueryError = unknown;

export function usePostTablesInventoryDevices<
  TData = Awaited<ReturnType<typeof postTablesInventoryDevices>>,
  TError = unknown,
>(
  postTablesInventoryDevicesBody: PostTablesInventoryDevicesBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryDevices>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventoryDevices>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventoryDevices>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventoryDevices<
  TData = Awaited<ReturnType<typeof postTablesInventoryDevices>>,
  TError = unknown,
>(
  postTablesInventoryDevicesBody: PostTablesInventoryDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryDevices>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventoryDevices>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventoryDevices>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventoryDevices<
  TData = Awaited<ReturnType<typeof postTablesInventoryDevices>>,
  TError = unknown,
>(
  postTablesInventoryDevicesBody: PostTablesInventoryDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryDevices>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Device Inventory
 */

export function usePostTablesInventoryDevices<
  TData = Awaited<ReturnType<typeof postTablesInventoryDevices>>,
  TError = unknown,
>(
  postTablesInventoryDevicesBody: PostTablesInventoryDevicesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventoryDevices>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesInventoryDevicesQueryOptions(
    postTablesInventoryDevicesBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * This table represent Vendor / Family overview in the whole network
 * @summary Vendor Families Overview
 */
export const postTablesInventorySummaryFamilies = (
  postTablesInventorySummaryFamiliesBody: PostTablesInventorySummaryFamiliesBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesInventorySummaryFamilies200>({
    url: `/tables/inventory/summary/families`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesInventorySummaryFamiliesBody,
    signal,
  });
};

export const getPostTablesInventorySummaryFamiliesQueryKey = (
  postTablesInventorySummaryFamiliesBody: PostTablesInventorySummaryFamiliesBody,
) => {
  return [
    `/tables/inventory/summary/families`,
    postTablesInventorySummaryFamiliesBody,
  ] as const;
};

export const getPostTablesInventorySummaryFamiliesQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
  TError = unknown,
>(
  postTablesInventorySummaryFamiliesBody: PostTablesInventorySummaryFamiliesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesInventorySummaryFamiliesQueryKey(
      postTablesInventorySummaryFamiliesBody,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>
  > = ({ signal }) =>
    postTablesInventorySummaryFamilies(
      postTablesInventorySummaryFamiliesBody,
      signal,
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesInventorySummaryFamiliesQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>
>;
export type PostTablesInventorySummaryFamiliesQueryError = unknown;

export function usePostTablesInventorySummaryFamilies<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
  TError = unknown,
>(
  postTablesInventorySummaryFamiliesBody: PostTablesInventorySummaryFamiliesBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventorySummaryFamilies<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
  TError = unknown,
>(
  postTablesInventorySummaryFamiliesBody: PostTablesInventorySummaryFamiliesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventorySummaryFamilies<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
  TError = unknown,
>(
  postTablesInventorySummaryFamiliesBody: PostTablesInventorySummaryFamiliesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Vendor Families Overview
 */

export function usePostTablesInventorySummaryFamilies<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
  TError = unknown,
>(
  postTablesInventorySummaryFamiliesBody: PostTablesInventorySummaryFamiliesBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryFamilies>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesInventorySummaryFamiliesQueryOptions(
    postTablesInventorySummaryFamiliesBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * This table represent Vendor / Family / Platform / Model overview in the whole network
 * @summary Vendor Models Overview
 */
export const postTablesInventorySummaryModels = (
  postTablesInventorySummaryModelsBody: PostTablesInventorySummaryModelsBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesInventorySummaryModels200>({
    url: `/tables/inventory/summary/models`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesInventorySummaryModelsBody,
    signal,
  });
};

export const getPostTablesInventorySummaryModelsQueryKey = (
  postTablesInventorySummaryModelsBody: PostTablesInventorySummaryModelsBody,
) => {
  return [
    `/tables/inventory/summary/models`,
    postTablesInventorySummaryModelsBody,
  ] as const;
};

export const getPostTablesInventorySummaryModelsQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
  TError = unknown,
>(
  postTablesInventorySummaryModelsBody: PostTablesInventorySummaryModelsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesInventorySummaryModelsQueryKey(
      postTablesInventorySummaryModelsBody,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesInventorySummaryModels>>
  > = ({ signal }) =>
    postTablesInventorySummaryModels(
      postTablesInventorySummaryModelsBody,
      signal,
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesInventorySummaryModelsQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesInventorySummaryModels>>
>;
export type PostTablesInventorySummaryModelsQueryError = unknown;

export function usePostTablesInventorySummaryModels<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
  TError = unknown,
>(
  postTablesInventorySummaryModelsBody: PostTablesInventorySummaryModelsBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventorySummaryModels>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventorySummaryModels<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
  TError = unknown,
>(
  postTablesInventorySummaryModelsBody: PostTablesInventorySummaryModelsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventorySummaryModels>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventorySummaryModels<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
  TError = unknown,
>(
  postTablesInventorySummaryModelsBody: PostTablesInventorySummaryModelsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Vendor Models Overview
 */

export function usePostTablesInventorySummaryModels<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
  TError = unknown,
>(
  postTablesInventorySummaryModelsBody: PostTablesInventorySummaryModelsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryModels>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesInventorySummaryModelsQueryOptions(
    postTablesInventorySummaryModelsBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * This table represent Vendor / Family / Platform overview in the whole network
 * @summary Vendor Platforms Overview
 */
export const postTablesInventorySummaryPlatforms = (
  postTablesInventorySummaryPlatformsBody: PostTablesInventorySummaryPlatformsBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesInventorySummaryPlatforms200>({
    url: `/tables/inventory/summary/platforms`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesInventorySummaryPlatformsBody,
    signal,
  });
};

export const getPostTablesInventorySummaryPlatformsQueryKey = (
  postTablesInventorySummaryPlatformsBody: PostTablesInventorySummaryPlatformsBody,
) => {
  return [
    `/tables/inventory/summary/platforms`,
    postTablesInventorySummaryPlatformsBody,
  ] as const;
};

export const getPostTablesInventorySummaryPlatformsQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
  TError = unknown,
>(
  postTablesInventorySummaryPlatformsBody: PostTablesInventorySummaryPlatformsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesInventorySummaryPlatformsQueryKey(
      postTablesInventorySummaryPlatformsBody,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>
  > = ({ signal }) =>
    postTablesInventorySummaryPlatforms(
      postTablesInventorySummaryPlatformsBody,
      signal,
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesInventorySummaryPlatformsQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>
>;
export type PostTablesInventorySummaryPlatformsQueryError = unknown;

export function usePostTablesInventorySummaryPlatforms<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
  TError = unknown,
>(
  postTablesInventorySummaryPlatformsBody: PostTablesInventorySummaryPlatformsBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventorySummaryPlatforms<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
  TError = unknown,
>(
  postTablesInventorySummaryPlatformsBody: PostTablesInventorySummaryPlatformsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventorySummaryPlatforms<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
  TError = unknown,
>(
  postTablesInventorySummaryPlatformsBody: PostTablesInventorySummaryPlatformsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Vendor Platforms Overview
 */

export function usePostTablesInventorySummaryPlatforms<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
  TError = unknown,
>(
  postTablesInventorySummaryPlatformsBody: PostTablesInventorySummaryPlatformsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryPlatforms>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesInventorySummaryPlatformsQueryOptions(
    postTablesInventorySummaryPlatformsBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * This table represent Vendor overview in the whole network
 * @summary Vendors Overview
 */
export const postTablesInventorySummaryVendors = (
  postTablesInventorySummaryVendorsBody: PostTablesInventorySummaryVendorsBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesInventorySummaryVendors200>({
    url: `/tables/inventory/summary/vendors`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesInventorySummaryVendorsBody,
    signal,
  });
};

export const getPostTablesInventorySummaryVendorsQueryKey = (
  postTablesInventorySummaryVendorsBody: PostTablesInventorySummaryVendorsBody,
) => {
  return [
    `/tables/inventory/summary/vendors`,
    postTablesInventorySummaryVendorsBody,
  ] as const;
};

export const getPostTablesInventorySummaryVendorsQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
  TError = unknown,
>(
  postTablesInventorySummaryVendorsBody: PostTablesInventorySummaryVendorsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesInventorySummaryVendorsQueryKey(
      postTablesInventorySummaryVendorsBody,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>
  > = ({ signal }) =>
    postTablesInventorySummaryVendors(
      postTablesInventorySummaryVendorsBody,
      signal,
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesInventorySummaryVendorsQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>
>;
export type PostTablesInventorySummaryVendorsQueryError = unknown;

export function usePostTablesInventorySummaryVendors<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
  TError = unknown,
>(
  postTablesInventorySummaryVendorsBody: PostTablesInventorySummaryVendorsBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventorySummaryVendors<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
  TError = unknown,
>(
  postTablesInventorySummaryVendorsBody: PostTablesInventorySummaryVendorsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
          TError,
          Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesInventorySummaryVendors<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
  TError = unknown,
>(
  postTablesInventorySummaryVendorsBody: PostTablesInventorySummaryVendorsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Vendors Overview
 */

export function usePostTablesInventorySummaryVendors<
  TData = Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
  TError = unknown,
>(
  postTablesInventorySummaryVendorsBody: PostTablesInventorySummaryVendorsBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesInventorySummaryVendors>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesInventorySummaryVendorsQueryOptions(
    postTablesInventorySummaryVendorsBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}
